import * as React from "react";
import { Command } from "cmdk";
import { SearchIcon } from "lucide-react";
import { cn } from "../../utils/cn";
import { Dialog, DialogContent, DialogTitle } from "./dialog";

export interface CommandPaletteAction {
  id: string;
  label: string;
  section?: string;
  keywords?: string[];
  disabled?: boolean;
  perform: () => void;
}

export interface CommandPaletteContextValue {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  registerAction: (action: CommandPaletteAction) => () => void;
  actions: CommandPaletteAction[];
}

const CommandPaletteContext = React.createContext<CommandPaletteContextValue | null>(null);

function CommandPaletteProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);
  const [actions, setActions] = React.useState<CommandPaletteAction[]>([]);

  const registerAction = React.useCallback((action: CommandPaletteAction) => {
    setActions((current) => [...current.filter((candidate) => candidate.id !== action.id), action]);
    return () => setActions((current) => current.filter((candidate) => candidate.id !== action.id));
  }, []);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((current) => !current);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const value = React.useMemo(() => ({ open, setOpen, registerAction, actions }), [actions, open, registerAction]);

  return <CommandPaletteContext.Provider value={value}>{children}</CommandPaletteContext.Provider>;
}

function useCommandPalette() {
  const context = React.useContext(CommandPaletteContext);
  if (!context) {
    throw new Error("useCommandPalette must be used within CommandPaletteProvider");
  }
  return context;
}

export interface CommandPaletteProps {
  title?: string;
  placeholder?: string;
  emptyText?: string;
  className?: string;
}

function CommandPalette({
  title = "Command palette",
  placeholder = "Search actions",
  emptyText = "No actions found",
  className,
}: CommandPaletteProps) {
  const { open, setOpen, actions } = useCommandPalette();
  const sections = React.useMemo(() => {
    const grouped = new Map<string, CommandPaletteAction[]>();
    actions.forEach((action) => {
      const key = action.section ?? "Actions";
      grouped.set(key, [...(grouped.get(key) ?? []), action]);
    });
    return Array.from(grouped.entries());
  }, [actions]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className={cn("overflow-hidden p-0", className)} showCloseButton={false}>
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <Command className="bg-popover text-popover-foreground">
          <div className="flex items-center gap-2 border-b px-3">
            <SearchIcon className="size-4 text-muted-foreground" />
            <Command.Input
              className="h-12 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              placeholder={placeholder}
            />
          </div>
          <Command.List className="max-h-80 overflow-y-auto p-2">
            <Command.Empty className="px-2 py-6 text-center text-sm text-muted-foreground">{emptyText}</Command.Empty>
            {sections.map(([section, sectionActions]) => (
              <Command.Group key={section} heading={section} className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground">
                {sectionActions.map((action) => (
                  <Command.Item
                    key={action.id}
                    value={[action.label, ...(action.keywords ?? [])].join(" ")}
                    disabled={action.disabled}
                    className="flex cursor-pointer items-center rounded-sm px-2 py-2 text-sm outline-none aria-selected:bg-accent aria-selected:text-accent-foreground data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50"
                    onSelect={() => {
                      action.perform();
                      setOpen(false);
                    }}
                  >
                    {action.label}
                  </Command.Item>
                ))}
              </Command.Group>
            ))}
          </Command.List>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

export { CommandPalette, CommandPaletteProvider, useCommandPalette };
